import { A, G } from '@mobily/ts-belt';
import DescriptionIcon from '@mui/icons-material/Description';
import { Link as MuiLink } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDataProvider, useNotify, useTranslate } from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';

import { FINANCIAL_INDEX_SHORT_NAME } from '@Common/constants/financial_indexes';
import { PostCalculateMatchingContributionsEndpoint } from '@Common/types/bo/b2b/campaigns';

import { bulkExportAllocations } from '@sh4res/bo-common/components/B2bCampaigns/bulkExportAllocations';
import { isCampaignVacationDaysAllocation } from '@sh4res/bo-common/components/B2bCampaigns/helpers/isCampaignVacationDaysAllocation';
import {
  AllocationEstimationsPayload,
  prepareAllocationEstimations,
} from '@sh4res/bo-common/components/B2bCampaigns/prepareAllocationEstimations';
import { AllocationRecord } from '@sh4res/bo-common/components/B2bCampaigns/steps/allocations/AllocationsConfirmationStep';
import { useFinancialIndexValue } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';

const findInstrumentName = (
  choices: AllocationRecord['company_collected_choices'],
  instrumentId: number | null,
): string | null | undefined =>
  choices?.find((choice) => choice.instrument_id === instrumentId)?.instrument_name;

export const ExportPendingCampaignAllocations = ({
  matchingContributions,
  allocations,
}: {
  matchingContributions?: PostCalculateMatchingContributionsEndpoint['Client']['ResponseBody']['matching_contributions'];
  allocations?: PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'];
}) => {
  const notify = useNotify();
  const translate = useTranslate();
  const { getValues } = useFormContext();
  const dataProvider = useDataProvider();
  const tmoPct = useFinancialIndexValue(FINANCIAL_INDEX_SHORT_NAME.TMO);

  const [isExporting, setIsExporting] = useState(false);

  const enrichAllocationsWithInstrumentNames = useCallback(
    async (formValues: FieldValues) => {
      const instrumentIds: number[] = [];
      formValues.allocations?.forEach((allocation: AllocationRecord) => {
        allocation.company_collected_choices?.forEach((choice) => {
          if (G.isNotNullable(choice.instrument_id) && !choice.instrument_name) {
            instrumentIds.push(choice.instrument_id);
          }
        });
      });

      const instrumentNames: Record<number, string> = {};
      if (A.isNotEmpty(instrumentIds)) {
        const uniqueInstrumentIds = [...new Set(instrumentIds)];
        const { data: instruments } = await dataProvider.getList('instruments', {
          filter: { id: uniqueInstrumentIds },
          pagination: { page: 1, perPage: uniqueInstrumentIds.length },
          sort: { field: 'id', order: 'ASC' },
        });

        instruments.forEach(
          (instrument: { id: number; short_name?: string; al_name?: string; symbol?: string }) => {
            instrumentNames[instrument.id] =
              instrument.short_name ||
              instrument.al_name ||
              instrument.symbol ||
              `Instrument ${instrument.id}`;
          },
        );
      }

      return {
        ...formValues,
        allocations: formValues.allocations?.map((allocation: AllocationRecord) => ({
          ...allocation,
          company_collected_choices: allocation.company_collected_choices?.map((choice) => ({
            ...choice,
            instrument_name:
              choice.instrument_name ||
              (G.isNotNullable(choice.instrument_id)
                ? instrumentNames[choice.instrument_id]
                : null),
          })),
        })),
      };
    },
    [dataProvider],
  );

  const handleTransform = useCallback(
    (
      data: FieldValues,
      allocsWithIds: PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'],
    ): Omit<AllocationEstimationsPayload, 'matching_contributions' | 'tmoPct'> => {
      const isVacationDaysAllocation = isCampaignVacationDaysAllocation(data);

      return {
        company_id: data.company_id,
        campaign: {
          type: data.type,
          end_date: data.end_date || new Date(),
          fiscal_period_start: data.fiscal_period_start,
          fiscal_period_end: data.fiscal_period_end,
          is_automatic_late_payment_interests:
            data.is_automatic_late_payment_interests === 'yes' ? true : false,
          late_payment_interests_override_amount:
            data.late_payment_interests_override_amount ?? null,
          amount_type_input: data.amount_type_input,
          choice_collected_by: data.choice_collected_by,
          value_sharing_bonus_fiscal_rule: data.value_sharing_bonus_fiscal_rule ?? null,
        },
        allocations: (allocsWithIds ?? []).map((allocation) => {
          const dataAllocation: AllocationRecord | undefined = data.allocations.find(
            (a: AllocationRecord) => a.employee_id === allocation.employee_id,
          );

          return {
            identifier: dataAllocation?.identifier ?? '',
            employee_id: allocation.employee_id as number,
            // Days allocations are sent to the backend with a zero amount (it computes them); the
            // amounts entered in the form are the ones to export.
            amount: isVacationDaysAllocation
              ? (dataAllocation?.amount ?? 0)
              : (allocation.amount ?? 0),
            company_collected_choices: (allocation.company_collected_choices || []).map(
              (choice) => {
                const plan_id =
                  choice.plan_id === 'payout' || typeof choice.plan_id !== 'number'
                    ? null
                    : choice.plan_id;
                const instrument_id =
                  typeof choice.instrument_id === 'number' ? choice.instrument_id : null;

                const instrument_name = findInstrumentName(
                  dataAllocation?.company_collected_choices,
                  instrument_id,
                );
                return { ...choice, plan_id, instrument_id, instrument_name };
              },
            ),
          };
        }),
        employees: data.allocations.map(
          (allocation: AllocationRecord): AllocationEstimationsPayload['employees'][number] => ({
            firstname: allocation.firstname,
            lastname: allocation.lastname,
            registration_number: allocation.registration_number,
            employee_id: allocation.employee_id,
            identifier: allocation.identifier,
            social_status: allocation.social_status,
            tax_residence_code: allocation.tax_residence_code,
          }),
        ),
      };
    },
    [],
  );

  const handleDownloadReport = useCallback(async () => {
    try {
      setIsExporting(true);
      const formValues = getValues();

      if (!allocations) {
        return null;
      }

      const enrichedFormValues = await enrichAllocationsWithInstrumentNames(formValues);

      const transformedData = handleTransform(enrichedFormValues, allocations);

      const exportData = prepareAllocationEstimations({
        ...transformedData,
        matching_contributions: matchingContributions,
        tmoPct,
      });

      await bulkExportAllocations({
        initialData: exportData,
        campaignType: formValues.type,
        dataProvider,
        documentName: translate(
          'resources.bo_common.b2b.campaigns.dashboard.allocations.export.file_name_draft',
          { date: new Date().toLocaleDateString() },
        ),
      });
    } catch (error) {
      notify(
        'resources.bo_common.b2b.campaigns.dashboard.allocations.export.errors.report_generation_failed',
        {
          type: 'error',
          messageArgs: { error: getErrorMessage(error) },
        },
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    allocations,
    enrichAllocationsWithInstrumentNames,
    getValues,
    handleTransform,
    matchingContributions,
    notify,
    tmoPct,
    translate,
    dataProvider,
  ]);

  return (
    <MuiLink
      component="button"
      type="button"
      onClick={handleDownloadReport}
      disabled={isExporting}
      underline="hover"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: isExporting ? 'default' : 'pointer',
        color: isExporting ? 'text.disabled' : 'primary.main',
        border: 'none',
        background: 'none',
        padding: 0,
        font: 'inherit',
      }}>
      <DescriptionIcon sx={{ fontSize: 20, verticalAlign: 'middle' }} />
      {translate('resources.bo_common.b2b.campaigns.dashboard.allocations.export.label')}
    </MuiLink>
  );
};
